import { Component, type ReactNode, type ErrorInfo } from "react";
import { RefreshCw } from "lucide-react";
import i18n from "@/i18n";
import { Sentry } from "@/lib/sentry";
import { isChunkLoadError } from "@/lib/lazyRetry";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardErrorState } from "@/components/dashboard/DashboardErrorState";

interface DashboardErrorBoundaryProps {
  children: ReactNode;
  /**
   * Route path or short identifier (e.g. "/dashboard", "/dashboard/bookings")
   * — recorded with the Sentry exception so the next occurrence is
   * debuggable from production logs alone (task #2350).
   */
  route?: string;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Task #2350 — defensive error boundary for every `/dashboard/*` page.
 *
 * Before this existed, a single throw anywhere inside `DashboardPage`
 * (the `useOwnerDashboard` hook, the host-tour overlay, the new
 * `DashboardEventsBanner` / `DashboardBlogFeed` blocks, etc.) silently
 * blanked the host dashboard with no JS error visible in the console —
 * because the only ancestor boundary was the root `ErrorBoundary` and
 * lazy-loaded chunks were swallowing the throw before it reached it on
 * staging.
 *
 * We render the boundary INSIDE the `DashboardLayout` shell so the host
 * sidebar / hat-switch stay reachable even when the inner content
 * crashes — the user can always click another sidebar entry to escape
 * the broken page instead of being stranded on a white screen.
 *
 * Sibling Task #2349 (admin-impersonation blank dashboard) coordinates
 * with this boundary: a single throw on either trigger now lands here
 * and is reported with route + user context.
 */
export class DashboardErrorBoundary extends Component<
  DashboardErrorBoundaryProps,
  DashboardErrorBoundaryState
> {
  constructor(props: DashboardErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): DashboardErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `[DashboardErrorBoundary${this.props.route ? `: ${this.props.route}` : ""}]`,
      error,
      errorInfo,
    );
    Sentry.captureException(error, {
      tags: {
        boundary: "dashboard",
        route: this.props.route || "unknown",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // A failed code-split chunk can't be recovered by resetting React state
      // (the module is missing from the booted bundle) — offer a true reload
      // that fetches the fresh deploy.
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <DashboardLayout>
          <div className="flex flex-col items-center gap-3 px-4">
            <DashboardErrorState
              title={
                chunkError
                  ? i18n.t("dashboard.errorBoundary.new_version_title", "A new version is available")
                  : i18n.t("dashboard.errorBoundary.snag_title", "We hit a snag loading this page")
              }
              description={
                chunkError
                  ? i18n.t("dashboard.errorBoundary.chunk_desc", "This page couldn't finish loading because the app was updated. Reload to get the latest version.")
                  : i18n.t("dashboard.errorBoundary.snag_desc", "Something went wrong while rendering your dashboard. You can try again. If the problem persists, please refresh.")
              }
            />
            <button
              type="button"
              onClick={chunkError ? () => window.location.reload() : this.handleRetry}
              data-testid="dashboard-error-retry"
              className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {chunkError ? i18n.t("dashboard.errorBoundary.reload", "Reload") : i18n.t("common.try_again")}
            </button>
          </div>
        </DashboardLayout>
      );
    }

    return this.props.children;
  }
}
